import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getEmployerJobs, updateJobStatus, deleteJob, JOB_STATUS } from '../services/jobService';
import { getEmployerApplications } from '../services/applicationService';
import JobCard from '../components/JobCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

const ManageJobs = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user || userData?.userType !== 'employer') {
      navigate('/dashboard');
      return;
    }

    // Check if filter was passed from dashboard
    if (location.state?.filter) {
      console.log('📌 [ManageJobs] Filter received from navigation:', location.state.filter);
      setStatusFilter(location.state.filter);
    }

    // Check if message was passed from PostJob
    if (location.state?.message) {
      console.log('📌 [ManageJobs] Message received from navigation:', location.state.message);
      setMessage(location.state.message);
      // Clear message after 5 seconds
      setTimeout(() => setMessage(''), 5000);
    }

    fetchJobs();
  }, [user, userData, navigate, location.state]);

  useEffect(() => {
    filterJobs();
  }, [jobs, statusFilter, searchTerm]);

  const fetchJobs = async () => {
    console.log('🔄 [ManageJobs] Fetching jobs and applications for user:', user.uid);
    setLoading(true);
    try {
      // Fetch jobs and applications in parallel
      const [jobsResult, applicationsResult] = await Promise.all([
        getEmployerJobs(user.uid, { limit: 100 }),
        getEmployerApplications(user.uid)
      ]);

      console.log('📊 [ManageJobs] Jobs result:', jobsResult);
      console.log('📊 [ManageJobs] Applications result:', applicationsResult);

      if (jobsResult.success) {
        // Additional filter to ensure only current user's jobs are shown
        const userJobs = jobsResult.jobs.filter(job => job.userId === user.uid);
        console.log('✅ [ManageJobs] User jobs count:', userJobs.length);
        setJobs(userJobs);
      } else {
        console.error('❌ [ManageJobs] Failed to fetch jobs:', jobsResult.error);
        setJobs([]);
      }

      if (applicationsResult.success) {
        console.log('✅ [ManageJobs] Applications count:', applicationsResult.applications.length);
        setApplications(applicationsResult.applications);
      } else {
        console.error('❌ [ManageJobs] Failed to fetch applications:', applicationsResult.error);
        setApplications([]);
      }
    } catch (error) {
      console.error('❌ [ManageJobs] Error fetching data:', error);
      setJobs([]);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  const handleStatusChange = async (jobId, newStatus) => {
    console.log('🔄 [ManageJobs] Updating job status:', jobId, newStatus);

    // Verify job ownership before updating
    const job = jobs.find(j => j.id === jobId);
    if (!job || job.userId !== user.uid) {
      alert('Error: You do not have permission to update this job.');
      console.error('❌ [ManageJobs] Unauthorized status update attempt');
      return;
    }

    try {
      const result = await updateJobStatus(jobId, newStatus, user.uid);
      if (result.success) {
        setJobs(jobs.map(job =>
          job.id === jobId ? { ...job, status: newStatus } : job
        ));
        console.log('✅ [ManageJobs] Job status updated successfully');
      } else {
        alert('Error updating job status: ' + result.error);
        console.error('❌ [ManageJobs] Status update failed:', result.error);
      }
    } catch (error) {
      alert('Error updating job status: ' + error.message);
      console.error('❌ [ManageJobs] Status update error:', error);
    }
  };

  const handleDeleteJob = async (jobId) => {
    console.log('🗑️ [ManageJobs] Attempting to delete job:', jobId);

    // Verify job ownership before deleting
    const job = jobs.find(j => j.id === jobId);
    if (!job || job.userId !== user.uid) {
      setMessage('Error: You do not have permission to delete this job.');
      console.error('❌ [ManageJobs] Unauthorized delete attempt');
      return;
    }

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${job.title}"? This action cannot be undone.`)) {
      console.log('ℹ️ [ManageJobs] Job deletion cancelled by user');
      return;
    }

    try {
      const result = await deleteJob(jobId, user.uid);
      if (result.success) {
        setJobs(jobs.filter(job => job.id !== jobId));
        setMessage('Job deleted successfully!');
        console.log('✅ [ManageJobs] Job deleted successfully');
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error deleting job: ' + result.error);
        console.error('❌ [ManageJobs] Delete failed:', result.error);
      }
    } catch (error) {
      setMessage('Error deleting job: ' + error.message);
      console.error('❌ [ManageJobs] Delete error:', error);
    }
  };

  const handleEditJob = (jobId) => {
    console.log('✏️ [ManageJobs] Editing job:', jobId);
    const job = jobs.find(j => j.id === jobId);
    if (!job || job.userId !== user.uid) {
      setMessage('Error: You do not have permission to edit this job.');
      return;
    }

    // Navigate to post-job page with job data for editing
    navigate('/post-job', {
      state: {
        editMode: true,
        jobData: job,
        jobId: jobId
      }
    });
  };

  const handleViewApplications = (jobId) => {
    console.log('👀 [ManageJobs] Viewing applications for job:', jobId);
    const job = jobs.find(j => j.id === jobId);
    if (!job) {
      setMessage('Job not found.');
      return;
    }

    // Navigate to applications page with job filter
    navigate('/applications', {
      state: {
        jobFilter: jobId,
        jobTitle: job.title
      }
    });
  };

  const getStatusCounts = () => {
    const counts = {
      all: jobs.length,
      active: jobs.filter(job => job.status === JOB_STATUS.ACTIVE).length,
      draft: jobs.filter(job => job.status === JOB_STATUS.DRAFT).length,
      paused: jobs.filter(job => job.status === JOB_STATUS.PAUSED).length,
      closed: jobs.filter(job => job.status === JOB_STATUS.CLOSED).length,
      expired: jobs.filter(job => job.status === JOB_STATUS.EXPIRED).length
    };
    return counts;
  };

  const getJobApplicationCount = (jobId) => {
    return applications.filter(app => app.jobId === jobId).length;
  };

  const handleStatusCardClick = (status) => {
    console.log('📊 [ManageJobs] Status card clicked:', status);
    setStatusFilter(status);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const statusCounts = getStatusCounts();

  return (
    <>
      <div className="min-h-screen bg-slate-50/50 relative overflow-hidden pb-24 lg:pb-0">
        {/* Background grid pattern */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(#0e3a61_1px,transparent_1px)] [background-size:24px_24px] opacity-5"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          
          {/* Header Banner */}
          <div className="bg-gradient-to-br from-[#0e3a61] to-[#051728] rounded-[2rem] rounded-tr-[4rem] rounded-bl-[4rem] shadow-xl border border-[#0e3a61]/10 overflow-hidden relative p-8 mb-8">
            {/* Glowing background highlights */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#e2822f]/10 rounded-full blur-2xl pointer-events-none"></div>
            
            <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6 z-10">
              <div className="flex-1 min-w-0">
                <span className="text-[10px] uppercase font-bold tracking-widest text-[#e2822f] bg-[#e2822f]/15 px-3 py-1 rounded-full border border-[#e2822f]/20">
                  Requisition Operations Center
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-3 tracking-tight">
                  Requisition Control Center
                </h1>
                <p className="mt-2 text-slate-300 text-xs sm:text-sm max-w-xl leading-relaxed">
                  Monitor, customize, and analyze your active hiring campaigns and pipeline funnels.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 flex-shrink-0">
                <Link
                  to="/dashboard"
                  className="text-white hover:text-[#e2822f] font-bold text-xs flex items-center gap-1.5 px-4 py-2.5 border border-white/10 hover:border-white/20 rounded-xl hover:bg-white/5 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Dashboard Command</span>
                </Link>
                <Link
                  to="/post-job"
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#e2822f] to-[#d97706] text-white font-bold text-xs rounded-xl hover:shadow-lg hover:shadow-[#e2822f]/20 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Deploy Requisition</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`mb-6 p-4 rounded-xl border ${message.toLowerCase().includes('error')
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-[#508d0c]/10 border-[#508d0c]/20 text-slate-800'
              }`}>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                {message.toLowerCase().includes('error') ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-[#508d0c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span>{message}</span>
              </div>
            </div>
          )}

          {/* Asymmetric Stats Metric Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            
            {/* Total Jobs */}
            <button
              onClick={() => handleStatusCardClick('all')}
              className={`bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full relative overflow-hidden group ${
                statusFilter === 'all' ? 'ring-2 ring-[#0e3a61] bg-[#0e3a61]/5' : ''
              }`}
            >
              <div className="flex justify-between items-start h-full flex-col min-h-[80px]">
                <div className="flex justify-between items-center w-full">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Aggregated</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    statusFilter === 'all' ? 'bg-[#0e3a61] text-white' : 'bg-slate-100 text-[#0e3a61]'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-extrabold text-[#0e3a61] tracking-tight mt-2">{statusCounts.all}</p>
                </div>
              </div>
            </button>

            {/* Active Jobs */}
            <button
              onClick={() => handleStatusCardClick(JOB_STATUS.ACTIVE)}
              className={`bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full border-l-4 border-l-[#508d0c] relative overflow-hidden group ${
                statusFilter === JOB_STATUS.ACTIVE ? 'ring-2 ring-[#508d0c] bg-[#508d0c]/5' : ''
              }`}
            >
              <div className="flex justify-between items-start h-full flex-col min-h-[80px]">
                <div className="flex justify-between items-center w-full">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Published</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    statusFilter === JOB_STATUS.ACTIVE ? 'bg-[#508d0c] text-white' : 'bg-[#508d0c]/10 text-[#508d0c]'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-extrabold text-[#508d0c] tracking-tight mt-2">{statusCounts.active}</p>
                </div>
              </div>
            </button>

            {/* Draft Jobs */}
            <button
              onClick={() => handleStatusCardClick(JOB_STATUS.DRAFT)}
              className={`bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full border-b-4 border-b-[#e2822f] relative overflow-hidden group ${
                statusFilter === JOB_STATUS.DRAFT ? 'ring-2 ring-[#e2822f] bg-[#e2822f]/5' : ''
              }`}
            >
              <div className="flex justify-between items-start h-full flex-col min-h-[80px]">
                <div className="flex justify-between items-center w-full">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Staged</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    statusFilter === JOB_STATUS.DRAFT ? 'bg-[#e2822f] text-white' : 'bg-[#e2822f]/10 text-[#e2822f]'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-extrabold text-[#e2822f] tracking-tight mt-2">{statusCounts.draft}</p>
                </div>
              </div>
            </button>

            {/* Paused Jobs */}
            <button
              onClick={() => handleStatusCardClick(JOB_STATUS.PAUSED)}
              className={`bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full border-t-4 border-t-amber-500 relative overflow-hidden group ${
                statusFilter === JOB_STATUS.PAUSED ? 'ring-2 ring-amber-500 bg-amber-50' : ''
              }`}
            >
              <div className="flex justify-between items-start h-full flex-col min-h-[80px]">
                <div className="flex justify-between items-center w-full">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Suspended</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    statusFilter === JOB_STATUS.PAUSED ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-600'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-extrabold text-amber-600 tracking-tight mt-2">{statusCounts.paused}</p>
                </div>
              </div>
            </button>

            {/* Closed Jobs */}
            <button
              onClick={() => handleStatusCardClick(JOB_STATUS.CLOSED)}
              className={`bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full border-r-4 border-r-red-500 relative overflow-hidden group ${
                statusFilter === JOB_STATUS.CLOSED ? 'ring-2 ring-red-500 bg-red-50' : ''
              }`}
            >
              <div className="flex justify-between items-start h-full flex-col min-h-[80px]">
                <div className="flex justify-between items-center w-full">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Archived</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    statusFilter === JOB_STATUS.CLOSED ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-extrabold text-red-600 tracking-tight mt-2">{statusCounts.closed}</p>
                </div>
              </div>
            </button>

            {/* Expired Jobs */}
            <button
              onClick={() => handleStatusCardClick(JOB_STATUS.EXPIRED)}
              className={`bg-white rounded-[2rem] rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left w-full relative overflow-hidden group ${
                statusFilter === JOB_STATUS.EXPIRED ? 'ring-2 ring-slate-400 bg-slate-100' : ''
              }`}
            >
              <div className="flex justify-between items-start h-full flex-col min-h-[80px]">
                <div className="flex justify-between items-center w-full">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Concluded</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    statusFilter === JOB_STATUS.EXPIRED ? 'bg-slate-400 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-extrabold text-slate-500 tracking-tight mt-2">{statusCounts.expired}</p>
                </div>
              </div>
            </button>
          </div>

          {/* Enhanced Search and Filters Panel */}
          <div className="bg-white rounded-[2rem] shadow-xl p-6 mb-8 border border-slate-100">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-3">
                  Search Requisitions
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Filter requisitions by title, domain, or regional office..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0e3a61] focus:border-transparent text-sm bg-slate-50/50"
                  />
                </div>
              </div>
              <div className="lg:w-64">
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-3">
                  Operational Status Selector
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0e3a61] focus:border-transparent text-sm bg-slate-50/50 font-semibold text-slate-700"
                >
                  <option value="all">All Status ({statusCounts.all})</option>
                  <option value={JOB_STATUS.ACTIVE}>Active ({statusCounts.active})</option>
                  <option value={JOB_STATUS.DRAFT}>Draft ({statusCounts.draft})</option>
                  <option value={JOB_STATUS.PAUSED}>Paused ({statusCounts.paused})</option>
                  <option value={JOB_STATUS.CLOSED}>Closed ({statusCounts.closed})</option>
                  <option value={JOB_STATUS.EXPIRED}>Expired ({statusCounts.expired})</option>
                </select>
              </div>
              {(searchTerm || statusFilter !== 'all') && (
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchTerm('');
                    }}
                    className="px-5 py-3 text-white bg-gradient-to-r from-[#0e3a61] to-[#08233c] hover:shadow-md font-bold text-xs rounded-xl transition-all duration-200 w-full lg:w-auto"
                  >
                    Reset Parameters
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Jobs List Card */}
          <div className="bg-white rounded-[2rem] shadow-xl overflow-hidden border border-slate-100">
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100/50">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-extrabold text-slate-950">Deployment Archive</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-bold text-slate-800">{filteredJobs.length}</span> of <span className="font-bold text-slate-800">{jobs.length}</span> requisitions active in pipeline
                    {statusFilter !== 'all' && ` • Filtered: ${statusFilter}`}
                    {searchTerm && ` • Query: "${searchTerm}"`}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {filteredJobs.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                    <svg className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">
                    {jobs.length === 0 ? 'Archive is vacant' : 'No records match search parameters'}
                  </h3>
                  <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">
                    {jobs.length === 0
                      ? 'Initialize your first requisition to activate candidates and aggregate pipeline responses.'
                      : 'Adjust your query strings or status filter settings to discover matching job profiles.'
                     }
                  </p>
                  {jobs.length === 0 ? (
                    <Link
                      to="/post-job"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#e2822f] to-[#d97706] text-white font-bold text-xs rounded-xl shadow-md hover:shadow-lg hover:shadow-orange-500/10 hover:-translate-y-0.5 transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Deploy First Requisition</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setSearchTerm('');
                      }}
                      className="px-6 py-3 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white font-bold text-xs rounded-xl transition-all duration-200"
                    >
                      Reset Parameters
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      showActions={true}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDeleteJob}
                      onEdit={handleEditJob}
                      onViewApplications={handleViewApplications}
                      applicationCount={getJobApplicationCount(job.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Hidden on mobile */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default ManageJobs;
